// Wallboard reasoning: what is broken, how badly, and whether the data can be
// trusted. Pure, so `bun run src/lib/wallboard.check.ts` can check it.
import type { Alert, Overview } from "./types";

export type NetState = "healthy" | "degraded" | "major";
export type Tier = "down" | "degraded" | "warn" | "healthy";

type OLT = Overview["olts"][number];
type PON = Overview["pons"][number];
type Down = Overview["onts_down"][number];

/**
 * One line of SERVICE IMPACT. Correlated: a dead OLT or PON is one incident
 * carrying its affected count, never a list of its ONTs.
 * Priority follows the NOC's order: OLT down, PON down, several ONTs on one
 * PON, single ONT.
 */
export type Incident =
  | { kind: "olt"; key: string; olt: OLT; affected: number; since: string | null; site: string | null }
  | { kind: "pon"; key: string; pon: PON; affected: number; since: string | null; site: string | null }
  | { kind: "multi"; key: string; pon: PON; affected: number; since: string | null; site: string | null }
  | { kind: "ont"; key: string; ont: Down; affected: 1; since: string | null; site: string | null; powerLoss: boolean };

const RANK: Record<Incident["kind"], number> = { olt: 0, pon: 1, multi: 2, ont: 3 };

/** A PON with this many or more ONTs, all offline, is a PON outage, not a set of customers. */
export const PON_DOWN_MIN = 2;
/** This many offline on one (otherwise working) PON are grouped into one incident. */
export const MULTI_MIN = 3;

const isOLTDown = (o: OLT) => o.status === "unreachable";
const offlineOf = (p: PON) => p.onts_total - p.onts_online;

export function incidents(d: Overview): Incident[] {
  const olts = new Map(d.olts.map((o) => [o.id, o]));
  const site = (oltId: number) => olts.get(oltId)?.location ?? null;
  // olt.unreachable alerts carry the outage start; the OLT row has none.
  const oltSince = (id: number) =>
    d.alerts_open.find((a) => a.metric === "olt.unreachable" && a.target_type === "olt" && a.target_id === id)?.opened_at ?? null;

  const out: Incident[] = [];
  const covered = new Set<string>(); // "oltId|pon_path" already explained by a bigger incident
  for (const o of d.olts.filter(isOLTDown)) {
    out.push({ kind: "olt", key: `olt${o.id}`, olt: o, affected: o.onts_total, since: oltSince(o.id), site: o.location });
  }
  for (const p of d.pons) {
    const olt = olts.get(p.olt_id);
    if (!olt || isOLTDown(olt)) { covered.add(`${p.olt_id}|${p.pon_path}`); continue; }
    const off = offlineOf(p);
    if (p.onts_online === 0 && p.onts_total >= PON_DOWN_MIN) {
      out.push({ kind: "pon", key: `pon${p.id}`, pon: p, affected: p.onts_total, since: p.last_down_at, site: site(p.olt_id) });
      covered.add(`${p.olt_id}|${p.pon_path}`);
    } else if (off >= MULTI_MIN) {
      out.push({ kind: "multi", key: `multi${p.id}`, pon: p, affected: off, since: p.last_down_at, site: site(p.olt_id) });
      covered.add(`${p.olt_id}|${p.pon_path}`);
    }
  }
  for (const o of d.onts_down) {
    if (covered.has(`${o.olt_id}|${o.pon_path}`)) continue;
    out.push({
      kind: "ont", key: `ont${o.id}`, ont: o, affected: 1, since: o.down_since, site: site(o.olt_id),
      powerLoss: !!o.last_down_cause?.toLowerCase().includes("gasp"),
    });
  }
  // Worst kind first, then the most customers, then the newest outage.
  return out.sort((a, b) =>
    RANK[a.kind] - RANK[b.kind] || b.affected - a.affected ||
    (Date.parse(b.since ?? "") || 0) - (Date.parse(a.since ?? "") || 0));
}

/**
 * Network posture from service conditions, never from the raw alert count:
 * an OLT or PON outage is a major incident; any customer offline or an OLT not
 * fully polled is degraded; warnings alone leave the network healthy.
 */
export function netState(d: Overview, inc: Incident[]): NetState {
  if (inc.some((i) => i.kind === "olt" || i.kind === "pon")) return "major";
  if (inc.length > 0 || d.olts.some((o) => o.status !== "reachable")) return "degraded";
  return "healthy";
}

// ponytail: 60/70 C are the GPON OLT SFP bands the old wallboard used; per-model limits would need the sensor's own.
export const HOT_C = 60;
export const HOT_CRIT_C = 70;

/**
 * Tiers mark CORRELATED trouble only. A few customers offline here and there
 * are SERVICE IMPACT rows, not an unhealthy PON/OLT/site: otherwise, at 50 OLTs
 * every box would be amber all day and the board would never be quiet.
 */
export function ponTier(p: PON): Tier {
  if (p.onts_online === 0 && p.onts_total >= PON_DOWN_MIN) return "down";
  if (offlineOf(p) >= MULTI_MIN) return "degraded";
  if (p.weak > 0) return "warn";
  return "healthy";
}

/** `pons` are the OLT's own; a PON outage or cluster makes its OLT degraded. */
export function oltTier(o: OLT, pons: PON[] = []): Tier {
  if (isOLTDown(o)) return "down";
  if (o.status !== "reachable") return "degraded";
  if (pons.some((p) => p.olt_id === o.id && (ponTier(p) === "down" || ponTier(p) === "degraded"))) return "degraded";
  if (o.temp_c !== null && o.temp_c >= HOT_C) return "warn";
  return "healthy";
}

const TIER_RANK: Record<Tier, number> = { down: 0, degraded: 1, warn: 2, healthy: 3 };

/** Abnormal first; within a tier, the most offline, then by name for a stable board. */
export function rankOLTs(olts: OLT[], pons: PON[] = []): OLT[] {
  return [...olts].sort((a, b) =>
    TIER_RANK[oltTier(a, pons)] - TIER_RANK[oltTier(b, pons)] || b.onts_offline - a.onts_offline || a.name.localeCompare(b.name));
}

export function rankPONs(pons: PON[]): PON[] {
  return [...pons].sort((a, b) =>
    TIER_RANK[ponTier(a)] - TIER_RANK[ponTier(b)] || offlineOf(b) - offlineOf(a) || b.weak - a.weak ||
    a.olt_name.localeCompare(b.olt_name) || a.pon_path.localeCompare(b.pon_path, undefined, { numeric: true }));
}

export type Site = {
  name: string | null;
  olts: OLT[];
  oltsUp: number;
  ontsTotal: number;
  ontsOnline: number;
  tier: Tier;
};

/** Sites only exist once an operator names a location on at least one OLT. */
export function sites(olts: OLT[], pons: PON[] = []): Site[] | null {
  if (!olts.some((o) => o.location)) return null;
  const by = new Map<string | null, OLT[]>();
  for (const o of olts) by.set(o.location, [...(by.get(o.location) ?? []), o]);
  return [...by.entries()]
    .map(([name, os]) => {
      const ranked = rankOLTs(os, pons);
      return {
        name,
        olts: ranked,
        oltsUp: os.filter((o) => !isOLTDown(o)).length,
        ontsTotal: os.reduce((n, o) => n + o.onts_total, 0),
        ontsOnline: os.reduce((n, o) => n + o.onts_online, 0),
        tier: oltTier(ranked[0], pons),
      };
    })
    // Same tier: the site with more customers offline first (a dark PON outranks a cluster).
    .sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier] ||
      (b.ontsTotal - b.ontsOnline) - (a.ontsTotal - a.ontsOnline) || (a.name ?? "~").localeCompare(b.name ?? "~"));
}

/**
 * Is the picture on the TV current? The OLT poller's heartbeat is its last
 * finished run, whatever the status: one dead OLT makes every run "error" yet
 * the rest of the data is fresh. Stale after three missed polls, or when the
 * board itself has not reached the API for a minute.
 */
export type Freshness = { stale: boolean; pollAgeS: number | null; reason: "ok" | "poller" | "api" | "never" };

export const STALE_POLLS = 3;
export const API_STALE_S = 60;

export function freshness(d: Overview, nowMs: number, fetchedAtMs: number, fetchFailing: boolean): Freshness {
  const run = d.collectors.find((c) => c.collector === "olt");
  const pollAgeS = run ? Math.max(0, Math.round((nowMs - Date.parse(run.finished_at)) / 1000)) : null;
  if (fetchFailing && (nowMs - fetchedAtMs) / 1000 > API_STALE_S) return { stale: true, pollAgeS, reason: "api" };
  if (pollAgeS === null) return { stale: true, pollAgeS, reason: "never" };
  if (pollAgeS > d.olt_poll_interval_s * STALE_POLLS) return { stale: true, pollAgeS, reason: "poller" };
  return { stale: false, pollAgeS, reason: "ok" };
}

const RX = ["ont.rx_power_dbm", "ont.olt_rx_dbm"];
/**
 * NETWORK WARNINGS: degradation that is not service impact and is not shown
 * elsewhere. Optical alerts live in OPTICAL HEALTH, ONT/OLT outages in SERVICE
 * IMPACT, so neither is repeated here.
 */
export function warningAlerts(alerts: Alert[]): Alert[] {
  return alerts.filter((a) => !RX.includes(a.metric) && a.metric !== "ont.run_state" && a.metric !== "olt.unreachable");
}

/**
 * How the open alerts split, from the uncapped per-metric counts: `other` is
 * what NETWORK WARNINGS owns (so it can say how many it could not list),
 * `optical` is what it hands to OPTICAL HEALTH. Info alerts are not warnings.
 */
export function alertBreakdown(d: Overview): { optical: number; other: number } {
  let optical = 0, other = 0;
  for (const m of d.alert_metrics) {
    if (m.severity === "info") continue;
    if (RX.includes(m.metric)) optical += m.n;
    else if (m.metric !== "ont.run_state" && m.metric !== "olt.unreachable") other += m.n;
  }
  return { optical, other };
}

/** "7m", "2h 05m", "3d 4h": duration on a TV, English units in both languages. */
export function dur(fromIso: string | null, nowMs: number): string {
  if (!fromIso) return "—";
  const s = Math.max(0, Math.floor((nowMs - Date.parse(fromIso)) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

// --- alarm -------------------------------------------------------------------

/** A key that sounded is not sounded again for this long: a flapping ONT rings once. */
export const ALARM_COOLDOWN_MS = 10 * 60_000;
/** An unacknowledged alarm repeats this often while its incidents are still open. */
export const ALARM_REPEAT_MS = 30_000;

/**
 * Incidents that should sound now: present, and not sounded within the
 * cooldown. Correlated like the board, so a dead PON is one alarm, not one per
 * ONT. `sounded` is updated in place (and pruned) with the ones returned.
 */
export function alarmable(inc: Incident[], sounded: Map<string, number>, nowMs: number): Incident[] {
  // Stamps are "last seen open": one older than the cooldown belongs to an
  // incident that cleared long enough ago to be news again.
  for (const [k, at] of sounded) if (nowMs - at > ALARM_COOLDOWN_MS) sounded.delete(k);
  const fresh = inc.filter((i) => !sounded.has(i.key));
  // Still-open incidents keep their stamp fresh, so the cooldown runs from when they cleared.
  for (const i of inc) sounded.set(i.key, nowMs);
  return fresh;
}

/** OLT and PON outages get the urgent tone; customer-level outages the short one. */
export const alarmLevel = (inc: Incident[]): "major" | "minor" =>
  inc.some((i) => i.kind === "olt" || i.kind === "pon") ? "major" : "minor";
